import React, { useEffect, useState } from "react";
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Typography,
    Tooltip
} from '@mui/material';
import { Box, Modal, Button } from '@mui/material';
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import plusIcon from '@iconify/icons-mdi/eye-circle-outline';
import editIcon from '@iconify/icons-mdi/square-edit-outline';
import deleteIcon from '@iconify/icons-mdi/trash-can-outline';
import closeIcon from '@iconify/icons-mdi/close-circle-outline';
import { getData, postData, deleteData, putData } from '../../../webService/webservice.js';
import TVFMLoader from "../Loader/loader.jsx";
import { useStore } from "../../../Context/context.jsx";
import AddSupplierForm from "./AddSupplierForm.jsx";
import "../../../styles/Auth/suppliers.css";
import StoreSingleAllStoresSelect, { ALL_STORE_OPTION } from "../../Challange/StoreSingleAllStoresSelect.jsx";
import Footer from "../Footer/Footer-page.jsx";
import { IconButton, Chip } from '@mui/material';

const DetailItem = ({ label, value, fullWidth, icon, color = "#3C0008" }) => (
    <Box sx={{
        gridColumn: fullWidth ? 'span 2' : 'span 1',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        pb: 1.5,
        mb: 0.5,
        borderBottom: '1px solid #f8fafc'
    }}>
        <Box sx={{
            color: color,
            display: 'flex',
            mt: 0.5,
            p: 1.2,
            borderRadius: '12px',
            bgcolor: `${color}15`,
            boxShadow: `0 4px 10px ${color}10`,
            border: `1px solid ${color}20`
        }}>
            {icon && <Icon icon={icon} style={{ fontSize: '18px' }} />}
        </Box>
        <Box sx={{ flex: 1 }}>
            <Typography
                variant="caption"
                sx={{
                    color: '#64748b',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    fontSize: '10px',
                    display: 'block',
                    mb: 0.2
                }}
            >
                {label}
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    color: '#1e293b',
                    fontWeight: 600,
                    wordBreak: 'break-word',
                    lineHeight: 1.4
                }}
            >
                {value || "-"}
            </Typography>
        </Box>
    </Box>
);

const SectionHeader = ({ icon, title, color = "#3C0008" }) => (
    <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        mb: 3,
        mt: 1,
        pb: 1,
        borderBottom: '2px solid #f1f5f9'
    }}>
        <Box sx={{
            display: 'flex',
            p: 1,
            borderRadius: '10px',
            bgcolor: color,
            color: '#ffffff',
            boxShadow: `0 8px 16px ${color}30`
        }}>
            {icon && <Icon icon={icon} style={{ fontSize: '18px' }} />}
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1a1e27' }}>
            {title}
        </Typography>
    </Box>
);
const SuppliersPage = () => {
    const role = localStorage.getItem("role");
    const sessionToken = localStorage.getItem("Token");
    const { selectedStoreId, setSelectedStoreId } = useStore();

    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [isAddSupplier, setIsAddSupplier] = useState(false);
    const [isEditSupplier, setIsEditSupplier] = useState(false);
    const [editSupplierData, setEditSupplierData] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [limit, setLimit] = useState(5);
    const [supplierToDelete, setSupplierToDelete] = useState(null);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [store, setStore] = useState(ALL_STORE_OPTION);
    const [isViewSupplier, setIsViewSupplier] = useState(false);
    const [viewSupplierData, setViewSupplierData] = useState(null);
    const [sortField, setSortField] = useState("createdAt");
    const [sortOrder, setSortOrder] = useState(-1);

    const handleSort = (field) => {
        const newOrder = sortField === field ? (sortOrder === 1 ? -1 : 1) : -1;
        setSortField(field);
        setSortOrder(newOrder);
    };
    const commonCellStyle = {
        color: '#525252',
        fontSize: '12px',
        fontWeight: 600,
        textAlign: 'center',
        border: 'none',
        padding: '12px 16px'
    };

    const headings = [
        'S.NO',
        'Supplier Name',
        'Contact Person',
        'Phone',
        'Email',
        'GST Number',
        'Action',
    ];

    const fetchSuppliers = async () => {
        try {
            const params = {
                page: currentPage,
                limit: limit === "all" ? 999999 : limit,
                search: searchQuery?.trim() || "",
            };
            const sid = role === "SuperAdmin" || role === "SelfAdmin" ? store?.value : Array.isArray(selectedStoreId)
                ? selectedStoreId[0]
                : selectedStoreId;
            if (sid && sid !== "all") {
                params.storeId = sid;
            }

            params.sort = JSON.stringify([[sortField, sortOrder]]);

            const response = await getData('suppliers/suppliers', params, sessionToken);
            if (response?.status !== 200) {
                toast.error(response?.message || "Failed to fetch Suppliers");
                return;
            }
            const resultsWrapper = response?.data?.[0] || {};
            const list = resultsWrapper?.paginatedResults || [];
            const count = resultsWrapper?.totalCount || 0;
            const pages = resultsWrapper?.totalPages || 1;

            setSuppliers(list);
            setTotalCount(count);
            setTotalPages(pages);
        } catch (error) {
            console.error(error);
            toast.error("Failed to fetch Suppliers");
        }
    };

    useEffect(() => {
        fetchSuppliers();
    }, [currentPage, limit, searchQuery, selectedStoreId, store, refreshKey, sortField, sortOrder]);

    const handleDeleteSupplier = async () => {
        try {
            const response = await deleteData(`suppliers/supplier/${supplierToDelete}`, sessionToken);
            toast.success(response.message || "Supplier deleted successfully");
            setOpenDeleteModal(false);
            setRefreshKey(prev => prev + 1);
        } catch (error) {
            toast.error("Failed to delete Supplier");
        }
    };

    const handleItemsPerPageChange = (e) => {
        setLimit(e.target.value);
        setCurrentPage(1);
    };

    const handlePageChange = (direction) => {
        if (direction === "prev" && currentPage > 1) {
            setCurrentPage(prev => prev - 1);
        } else if (direction === "next" && currentPage < totalPages) {
            setCurrentPage(prev => prev + 1);
        } else if (direction === "first") {
            setCurrentPage(1);
        } else if (direction === "last") {
            setCurrentPage(totalPages);
        }
    };

    const handleOpen = (supplierId) => {
        setSupplierToDelete(supplierId);
        setOpenDeleteModal(true);
    };

    const handleClose = () => {
        setOpenDeleteModal(false);
        setSupplierToDelete(null);
    };

    return (
        <div className="product-layout">
            <div className="product-content">
                <main className="product-main">
                    {isAddSupplier ? (
                        <AddSupplierForm
                            initialStoreId={selectedStoreId !== "all" ? selectedStoreId : null}
                            onSuccess={() => {
                                setIsAddSupplier(false);
                                setRefreshKey(prev => prev + 1);
                            }}
                            onCancel={() => setIsAddSupplier(false)}
                        />
                    ) : (
                        <>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Suppliers
                            </Typography>
                            <div className="product-widgets">
                                <div className="search-input-wrapper">
                                    <input
                                        type="text"
                                        placeholder="Search suppliers..."
                                        className="supplier-search-input"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                    <Icon icon="mdi:magnify" className="search-icon" />
                                </div>
                                {(role === "SuperAdmin" || role === "SelfAdmin") && (
                                    <StoreSingleAllStoresSelect
                                        value={store}
                                        onChange={(store) => setStore(store)}
                                    />
                                )}
                                <div className="add-btnsupplier">
                                    <button
                                        className="add-Product-button"
                                        onClick={() => setIsAddSupplier(true)}
                                    >
                                        + Add Supplier
                                    </button>
                                </div>

                            </div>

                            <Box component={Paper}>
                                <TableContainer sx={{
                                    maxHeight: 400,
                                    overflowY: "auto",
                                    '&::-webkit-scrollbar': { display: 'none' },
                                    scrollbarWidth: 'none',
                                    msOverflowStyle: 'none',
                                }}>
                                    <Table className="custom-product-table">
                                        <TableHead className="custom-product-head">
                                            <TableRow>

                                                {headings.map((heading) => {
                                                    const isSupplierName = heading === "Supplier Name";
                                                    const isContactPerson = heading === "Contact Person";
                                                    const isPhone = heading === "Phone";
                                                    const isEmail = heading === "Email";
                                                    const isGST = heading === "GST Number";

                                                    const sortKey = isSupplierName
                                                        ? "supplierName" : isContactPerson
                                                            ? "contactPerson" : isPhone
                                                                ? "phoneNumber" : isEmail
                                                                    ? "email" : isGST
                                                                        ? "gstNumber" : null;

                                                    return (
                                                        <TableCell
                                                            key={heading}
                                                            sx={{
                                                                border: "none",
                                                                padding: "12px 16px",
                                                                textAlign: "center",
                                                                position: "sticky",
                                                                top: 0,
                                                                zIndex: 0,
                                                                backgroundColor: '#3C0008'
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    gap: "4px",
                                                                    color: "#ffffff",
                                                                    fontSize: "14px",
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {heading}
                                                                {sortKey && (
                                                                    <span
                                                                        onClick={() => handleSort(sortKey)}
                                                                        style={{
                                                                            fontSize: "10px",
                                                                            color: "#fff",
                                                                            cursor: "pointer",
                                                                            marginLeft: "4px",
                                                                            userSelect: "none",
                                                                        }}
                                                                    >
                                                                        {sortField === sortKey
                                                                            ? sortOrder === 1
                                                                                ? "▲"
                                                                                : "▼"
                                                                            : "▲▼"}
                                                                    </span>
                                                                )}
                                                            </Typography>
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {loading ? (
                                                <TableRow>
                                                    <TableCell colSpan={headings.length} align="center">
                                                        <TVFMLoader />
                                                    </TableCell>
                                                </TableRow>
                                            ) : suppliers.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={headings.length} align="center">
                                                        No Suppliers Found
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                suppliers.map((supplier, index) => (
                                                    <TableRow key={supplier._id}>
                                                        <TableCell>
                                                            <Typography sx={commonCellStyle}>
                                                                {(currentPage - 1) * (limit === 'all' ? 0 : limit) + index + 1}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell><Typography sx={commonCellStyle}>{supplier.supplierName}</Typography></TableCell>
                                                        <TableCell><Typography sx={commonCellStyle}>{supplier.contactPerson}</Typography></TableCell>
                                                        <TableCell><Typography sx={commonCellStyle}>{supplier.phoneNumber}</Typography></TableCell>
                                                        <TableCell><Typography sx={commonCellStyle}>{supplier.email}</Typography></TableCell>
                                                        <TableCell><Typography sx={commonCellStyle}>{supplier.gstNumber}</Typography></TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', justifyContent: 'center' }}>
                                                                <Tooltip title="View Supplier">
                                                                    <Icon
                                                                        icon={plusIcon}
                                                                        width={20}
                                                                        height={20}
                                                                        style={{ cursor: 'pointer', color: '#3C0008', marginRight: '10px' }}
                                                                        onClick={() => {
                                                                            setViewSupplierData(supplier);
                                                                            setIsViewSupplier(true);
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                                <Tooltip title="Edit Supplier">
                                                                    <Icon
                                                                        icon={editIcon}
                                                                        width={20}
                                                                        height={20}
                                                                        style={{ cursor: 'pointer', color: '#3C0008', marginRight: '10px' }}
                                                                        onClick={() => {
                                                                            setEditSupplierData(supplier);
                                                                            setIsEditSupplier(true);
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                                <Tooltip title="Delete Supplier">
                                                                    <Icon
                                                                        icon={deleteIcon}
                                                                        width={20}
                                                                        height={20}
                                                                        style={{ cursor: 'pointer', color: '#C10000' }}
                                                                        onClick={() => {
                                                                            setSupplierToDelete(supplier._id);
                                                                            setOpenDeleteModal(true);
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <div className="supplier-bottom">
                                    <div className="show-entries">
                                        <span className="text-label">Show</span>
                                        <select className="entries-select" value={limit} onChange={handleItemsPerPageChange}>
                                            <option value="5">5</option>
                                            <option value="10">10</option>
                                            <option value="25">25</option>
                                            <option value="all">All</option>
                                        </select>
                                        <span className="text-label">Entries</span>
                                    </div>
                                    <div className="showing-range">
                                        {limit === 'all'
                                            ? `Showing all ${totalCount} entries`
                                            : `Showing ${(currentPage - 1) * limit + 1} to ${Math.min(currentPage * limit, totalCount)} of ${totalCount} entries`
                                        }
                                    </div>
                                    <div className="pagination-buttons">
                                        <button
                                            className="pagination-btn"
                                            disabled={currentPage === 1}
                                            onClick={() => handlePageChange("first")}
                                        >
                                            <Icon icon="mdi:chevron-double-left" />
                                        </button>
                                        <button
                                            className="pagination-btn"
                                            disabled={currentPage === 1}
                                            onClick={() => handlePageChange("prev")}
                                        >
                                            <Icon icon="mdi:chevron-left" />
                                        </button>
                                        <button
                                            className="pagination-btn"
                                            disabled={currentPage === totalPages || totalPages === 0}
                                            onClick={() => handlePageChange("next")}
                                        >
                                            <Icon icon="mdi:chevron-right" />
                                        </button>
                                        <button
                                            className="pagination-btn"
                                            disabled={currentPage === totalPages || totalPages === 0}
                                            onClick={() => handlePageChange("last")}
                                        >
                                            <Icon icon="mdi:chevron-double-right" />
                                        </button>
                                    </div>
                                </div>
                            </Box>
                        </>
                    )}
                </main>

                {isEditSupplier && (
                    <div className="popup-overlay">
                        <div className="popup-wrapper" style={{ maxWidth: '900px', width: '95%' }}>
                            <Icon
                                icon={closeIcon}
                                className="popup-close-icon-outside"
                                onClick={() => setIsEditSupplier(false)}
                            />
                            <div className="popup-form" style={{ maxHeight: '85vh', overflowY: 'auto', padding: '20px' }}>
                                <AddSupplierForm
                                    isEditMode={true}
                                    editData={editSupplierData}
                                    onSuccess={() => {
                                        setIsEditSupplier(false);
                                        setRefreshKey(prev => prev + 1);
                                    }}
                                    onCancel={() => setIsEditSupplier(false)}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <Modal
                open={openDeleteModal}
                onClose={handleClose}
                aria-labelledby="modal-modal-title"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 380,
                        background: "linear-gradient(135deg, #ffffff, #faf5f6)",
                        borderRadius: "20px",
                        boxShadow: "0 25px 70px rgba(0,0,0,0.35)",
                        p: 4,
                        outline: "none",
                        textAlign: "center",
                        overflow: "hidden"
                    }}
                >
                    <Box
                        sx={{
                            width: 74,
                            height: 74,
                            borderRadius: "50%",
                            background: "#3C0008",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 16px auto",
                            color: "#fff",
                            fontSize: 30,
                            boxShadow: "0 12px 28px rgba(26,30,39,0.55)",
                            animation: "deletePulse 1.6s infinite ease-in-out",
                            "@keyframes deletePulse": {
                                "0%": { transform: "scale(1)", boxShadow: "0 0 0 0 rgba(26,30,39,0.7)" },
                                "70%": { transform: "scale(1.1)", boxShadow: "0 0 0 18px rgba(26,30,39,0)" },
                                "100%": { transform: "scale(1)", boxShadow: "0 0 0 0 rgba(26,30,39,0)" },
                            },
                        }}
                    >
                        🗑️
                    </Box>

                    <Typography
                        id="modal-modal-title"
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: "#2b2b2b",
                            letterSpacing: "0.3px"
                        }}
                    >
                        Confirm Delete
                    </Typography>

                    <Typography
                        id="modal-modal-description"
                        sx={{
                            mt: 1.2,
                            fontSize: 14,
                            color: "#666",
                            lineHeight: 1.6
                        }}
                    >
                        Are you sure you want to delete this supplier?
                    </Typography>

                    <Box sx={{ display: "flex", justifyContent: "center", gap: 2.5, mt: 3.5 }}>
                        <Button
                            variant="contained"
                            onClick={handleDeleteSupplier}
                            className="add-Product-button"
                        >
                            Yes, Delete
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            className="cancel-Product-button"
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Modal
                open={isViewSupplier}
                onClose={() => setIsViewSupplier(false)}
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '80%', md: 750 },
                    maxHeight: '85vh',
                    bgcolor: '#ffffff',
                    borderRadius: '24px',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    outline: 'none',
                    animation: 'modalSlideUp 0.4s ease-out',
                    '@keyframes modalSlideUp': {
                        '0%': { transform: 'translate(-50%, -40%)', opacity: 0 },
                        '100%': { transform: 'translate(-50%, -50%)', opacity: 1 }
                    }
                }}>
                    {/* Top Banner & Header */}
                    <Box sx={{
                        height: '140px',
                        background: 'linear-gradient(135deg, #3C0008 0%, #a52a2a 100%)',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'flex-end',
                        px: 4,
                        pb: 2
                    }}>
                        <IconButton
                            onClick={() => setIsViewSupplier(false)}
                            sx={{ position: 'absolute', top: 16, right: 16, color: 'white', '&:hover': { background: 'rgba(255,255,255,0.1)' } }}
                        >
                            <Icon icon={closeIcon} />
                        </IconButton>

                        <Box sx={{
                            position: 'absolute',
                            bottom: '-50px',
                            left: '40px',
                            width: '120px',
                            height: '120px',
                            borderRadius: '24px',
                            border: '4px solid #ffffff',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                            overflow: 'hidden',
                            bgcolor: '#f8f9fa',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Icon icon="mdi:truck-delivery-outline" style={{ fontSize: '60px', color: '#3C0008' }} />
                        </Box>

                        <Box sx={{ ml: '140px', mb: 1 }}>
                            <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                                {viewSupplierData?.supplierName}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                    label="Supplier"
                                    size="small"
                                    sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600, fontSize: '12px' }}
                                />
                                <Box sx={{ display: 'flex', alignItems: 'center', color: 'rgba(255,255,255,0.7)', gap: 0.5 }}>
                                    <Icon icon="mdi:account-tie" style={{ fontSize: '16px' }} />
                                    <Typography variant="caption" fontWeight={600}>
                                        {viewSupplierData?.contactPerson}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Modal Content */}
                    <Box sx={{ mt: '60px', p: 4, overflowY: 'auto', flex: 1, bgcolor: '#f8fafc' }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
                            {/* Section 1: Business Details */}
                            <Box>
                                <SectionHeader icon="mdi:briefcase-outline" title="Business Details" color="#3C0008" />
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, bgcolor: '#ffffff', p: 2, borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                                    <DetailItem label="Supplier Name" value={viewSupplierData?.supplierName} icon="mdi:store-outline" color="#3C0008" />
                                    <DetailItem label="Contact Person" value={viewSupplierData?.contactPerson} icon="mdi:account-outline" color="#3C0008" />
                                    <DetailItem label="GST Number" value={viewSupplierData?.gstNumber} icon="mdi:card-account-details-outline" color="#3C0008" />
                                    <DetailItem label="Store" value={viewSupplierData?.storeName || (viewSupplierData?.storeId?.storeName)} icon="mdi:map-marker-radius-outline" color="#3C0008" />
                                </Box>
                            </Box>

                            {/* Section 2: Contact Information */}
                            <Box>
                                <SectionHeader icon="mdi:phone-outline" title="Contact Info" color="#3C0008" />
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, bgcolor: '#ffffff', p: 2, borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                                    <DetailItem label="Phone Number" value={viewSupplierData?.phoneNumber} icon="mdi:phone-classic" color="#3C0008" />
                                    <DetailItem label="Email Address" value={viewSupplierData?.email} icon="mdi:email-outline" color="#3C0008" />
                                    <DetailItem label="City" value={viewSupplierData?.city} icon="mdi:city-variant-outline" color="#3C0008" />
                                    <DetailItem label="State" value={viewSupplierData?.state} icon="mdi:map-outline" color="#3C0008" />
                                </Box>
                            </Box>

                            {/* Section 3: Full Address */}
                            <Box sx={{ gridColumn: 'span 2' }}>
                                <SectionHeader icon="mdi:map-marker-outline" title="Address Details" color="#3C0008" />
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, bgcolor: '#ffffff', p: 2, borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                                    <DetailItem label="Address" value={viewSupplierData?.address} icon="mdi:home-outline" fullWidth color="#3C0008" />
                                    <DetailItem label="Country" value={viewSupplierData?.country} icon="mdi:earth" color="#3C0008" />
                                    <DetailItem label="Pincode" value={viewSupplierData?.pincode} icon="mdi:mailbox-outline" color="#3C0008" />
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Actions Footer */}
                    <Box sx={{ px: 4, py: 2, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', bgcolor: '#f8fafc' }}>
                        <Button
                            variant="contained"
                            onClick={() => setIsViewSupplier(false)}
                            sx={{
                                background: '#3C0008',
                                borderRadius: '12px',
                                px: 4,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '14px',
                                '&:hover': { background: '#6d000e', boxShadow: '0 4px 12px rgba(60,0,8,0.2)' }
                            }}
                        >
                            Close
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Footer />
        </div>
    );
};

export default SuppliersPage;
